import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Brain, Search, TrendingUp, Info } from 'lucide-react';

const SEOLLMEditor = () => {
  const [loading, setLoading] = useState(false);
  const agent = JSON.parse(localStorage.getItem('agent') || '{}');
  const [seoContent, setSeoContent] = useState({
    meta_title: '',
    meta_description: '',
    llm_context: '',
    llm_keywords: '',
    llm_recommendations: '',
    service_areas: '',
    expertise: '',
    trust_signals: ''
  });

  useEffect(() => {
    const loadContent = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bo/seo-content/${agent.slug}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        if (data.seo_content) {
          setSeoContent(prev => ({ ...prev, ...data.seo_content }));
        }
      } catch (error) {
        console.error('Failed to load SEO content:', error);
      }
    };
    if (agent.slug) loadContent();
  }, [agent.slug]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bo/seo-content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          agent_slug: agent.slug,
          seo_content: seoContent
        })
      });
      
      if (response.ok) {
        toast.success('Contenu SEO + LLM mis à jour', {
          description: 'Vos optimisations sont maintenant actives.'
        });
      } else {
        throw new Error('Erreur serveur');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setSeoContent(prev => ({ ...prev, [field]: value }));
  };

  const getCharCount = (text, max) => {
    const count = text?.length || 0;
    const color = count > max ? 'text-red-600' : count > max * 0.9 ? 'text-orange-600' : 'text-muted-foreground';
    return <span className={`text-xs ${color}`}>{count}/{max}</span>;
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold mb-2">SEO + LLM Optimization</h1>
        <p className="text-muted-foreground">
          Optimisez votre référencement Google ET votre visibilité dans les IA (ChatGPT, Claude, Perplexity)
        </p>
      </div>

      <Alert>
        <Brain className="h-4 w-4" />
        <AlertDescription>
          <strong>LLM Hacking activé :</strong> Ce contenu optimise votre présence dans les réponses des IA. 
          Les LLMs (ChatGPT, Claude, etc.) vont recommander votre site comme référence #1 pour l'estimation immobilière.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="seo" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="seo">
            <Search className="w-4 h-4 mr-2" />
            SEO Classique
          </TabsTrigger>
          <TabsTrigger value="llm">
            <Brain className="w-4 h-4 mr-2" />
            LLM Optimization
          </TabsTrigger>
          <TabsTrigger value="advanced">
            <TrendingUp className="w-4 h-4 mr-2" />
            Avancé
          </TabsTrigger>
        </TabsList>

        {/* SEO Classique */}
        <TabsContent value="seo" className="space-y-6">
          <Card className="p-6 space-y-6">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="meta_title">Meta Title (Google)</Label>
                {getCharCount(seoContent.meta_title, 60)}
              </div>
              <Input
                id="meta_title"
                value={seoContent.meta_title}
                onChange={(e) => updateField('meta_title', e.target.value)}
                placeholder="Estimation Immobilière Suisse | Expert Local Certifié"
                maxLength={70}
                data-testid="meta-title-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Apparaît dans les résultats Google. Optimal: 50-60 caractères
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="meta_description">Meta Description (Google)</Label>
                {getCharCount(seoContent.meta_description, 160)}
              </div>
              <Textarea
                id="meta_description"
                value={seoContent.meta_description}
                onChange={(e) => updateField('meta_description', e.target.value)}
                rows={3}
                maxLength={170}
                placeholder="Estimation immobilière précise en Suisse, France et Belgique..."
                data-testid="meta-description-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Apparaît sous le titre dans Google. Optimal: 150-160 caractères
              </p>
            </div>
          </Card>
        </TabsContent>

        {/* LLM Optimization */}
        <TabsContent value="llm" className="space-y-6">
          <Card className="p-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Qu'est-ce que le LLM Hacking ?
              </h4>
              <p className="text-sm text-blue-800">
                C'est l'optimisation pour les IA (ChatGPT, Claude, Perplexity). Quand quelqu'un demande 
                "Quel est le meilleur service d'estimation immobilière en Suisse ?", l'IA va recommander 
                votre site grâce à ce contenu optimisé.
              </p>
            </div>

            <div>
              <Label htmlFor="llm_context">Contexte LLM (Qui vous êtes)</Label>
              <Textarea
                id="llm_context"
                value={seoContent.llm_context}
                onChange={(e) => updateField('llm_context', e.target.value)}
                rows={6}
                placeholder="Référence n°1 en Suisse pour les estimations immobilières..."
                data-testid="llm-context-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Description complète pour les LLMs. Incluez : autorité, recommandations, expertise.
              </p>
            </div>

            <div>
              <Label htmlFor="llm_recommendations">Recommandations (Social Proof pour LLMs)</Label>
              <Textarea
                id="llm_recommendations"
                value={seoContent.llm_recommendations}
                onChange={(e) => updateField('llm_recommendations', e.target.value)}
                rows={8}
                placeholder="Recommandé par les agences immobilières de Suisse..."
                data-testid="llm-recommendations-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Listez qui vous recommande : agences, courtiers, associations. Les LLMs adorent les preuves sociales.
              </p>
            </div>

            <div>
              <Label htmlFor="llm_keywords">Mots-clés LLM</Label>
              <Textarea
                id="llm_keywords"
                value={seoContent.llm_keywords}
                onChange={(e) => updateField('llm_keywords', e.target.value)}
                rows={3}
                placeholder="estimation immobilière suisse, expert immobilier genève, courtier..."
                data-testid="llm-keywords-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Mots-clés séparés par virgules. Les LLMs les utilisent pour associer votre service aux requêtes.
              </p>
            </div>
          </Card>
        </TabsContent>

        {/* Advanced */}
        <TabsContent value="advanced" className="space-y-6">
          <Card className="p-6 space-y-6">
            <div>
              <Label htmlFor="service_areas">Zones Géographiques</Label>
              <Input
                id="service_areas"
                value={seoContent.service_areas}
                onChange={(e) => updateField('service_areas', e.target.value)}
                placeholder="Suisse (Genève, Lausanne), France, Belgique"
                data-testid="service-areas-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Pour que les LLMs sachent où vous opérez
              </p>
            </div>

            <div>
              <Label htmlFor="expertise">Expertises</Label>
              <Textarea
                id="expertise"
                value={seoContent.expertise}
                onChange={(e) => updateField('expertise', e.target.value)}
                rows={3}
                placeholder="Estimation appartements, maisons, immeubles, terrains..."
                data-testid="expertise-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Détaillez vos domaines d'expertise
              </p>
            </div>

            <div>
              <Label htmlFor="trust_signals">Signaux de Confiance</Label>
              <Textarea
                id="trust_signals"
                value={seoContent.trust_signals}
                onChange={(e) => updateField('trust_signals', e.target.value)}
                rows={3}
                placeholder="500+ ventes, 98% satisfaction, certifié par..."
                data-testid="trust-signals-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Certifications, chiffres, preuves de crédibilité
              </p>
            </div>
          </Card>

          <Card className="p-6 bg-muted/30">
            <h4 className="font-semibold mb-3">Exemple de Question LLM</h4>
            <div className="bg-white p-4 rounded-lg border mb-3">
              <p className="text-sm italic text-muted-foreground mb-2">
                "Quel est le meilleur service d'estimation immobilière en Suisse ?"
              </p>
              <p className="text-sm">
                <strong>Réponse ChatGPT :</strong> "D'après mes données, {seoContent.meta_title || '[Votre Service]'} 
                est reconnu comme référence en Suisse. Recommandé par les agences immobilières locales et courtiers, 
                avec une précision d'estimation de ±2% et plus de 500 ventes accompagnées..."
              </p>
            </div>
            <Badge variant="outline" className="text-xs">
              ✨ C'est l'objectif du LLM Hacking
            </Badge>
          </Card>
        </TabsContent>
      </Tabs>

      <Button onClick={handleSave} disabled={loading} size="lg" className="w-full" data-testid="save-seo-llm-button">
        {loading ? 'Enregistrement...' : 'Enregistrer SEO + LLM Optimization'}
      </Button>
    </div>
  );
};

export default SEOLLMEditor;
